  "use client";
import React, { ReactNode, useEffect, useState } from "react";
import Icon from "@/components/icons/Icons";
import CardRotator from "../animated-cards/CardRotator";
import CardStackAnimation from "../animated-cards/CardStackAnimation";
import RiskScannerAnimation from "../animated-cards/RiskScannerAnimation";
import AnimatedFakeChart from "../animated-cards/AnimatedFakeChart";
import { AnimatePresence, motion } from "framer-motion";

const steps = [
  {
    id: "01",
    title: "Choose Your Service Model",
    description: "Pick the service that fits where you are right now - your investment stage, goals, and risk comfort.",
    image: "/next_assets/images/portfolio-services/Container(7).png",
  },
  {
    id: "02",
    title: "One Small Fee, Fully Invested in Your Name ",
    description: "Your fee doesn't disappear, it's invested in a debt fund in your name, so our analyst stays fully aligned with your outcome.",
    image: "/next_assets/images/portfolio-services/Container(6).png",
  },
  {
    id: "03",
    title: "Risk Profiling & Deep Portfolio Intelligence Session",
    description: "Understand your risks, goals, & opportunities through a structured expert session.",
    image: "/next_assets/images/portfolio-services/Container(8).png",
  },
  {
    id: "04",
    title: "Get Your Personalised Portfolio Plan",
    description: "A clear, actionable portfolio strategy built around your goals and risk profile.",
    image: "/next_assets/images/portfolio-services/Container(9).png",
  },
];

const BG_MAPPING = ["bg-[#FFF6EE]", "bg-[#EEFFF1]", "bg-[#F5EEFF]", "bg-[#EEF1FF]"];

const DOTS_MAPPING = [
  {
    id: 0,
    style:
      "hidden xs:inline-block bottom-[16%] left-[9%] sm:left-[unset] sm:bottom-[unset] sm:top-[6%] sm:right-[16.5%]",
  },
  {
    id: 1,
    style: "hidden xs:inline-block bottom-[2%] left-[32%] sm:left-[unset] sm:bottom-[unset] sm:top-[32%] sm:right-[2%]",
  },
  { id: 2, style: "hidden xs:inline-block bottom-[2%] right-[32%] sm:bottom-[32%] sm:right-[2%]" },
  { id: 3, style: "hidden xs:inline-block bottom-[16%] right-[9%] sm:bottom-[6%] sm:right-[16.4%]" },
];

const STEP_ANIMATIONS: Record<number, ReactNode> = {
  0: <CardRotator />,
  1: <CardStackAnimation />,
  2: <RiskScannerAnimation />,
  3: <AnimatedFakeChart />,
};

const STEP_WRAPPER_CLASS: Record<number, string> = {
  0: "absolute inset-x-0 bottom-[20%] sm:inset-y-0 sm:left-[unset] sm:right-[12%] mx-auto sm:mx-0 sm:my-auto w-80 sm:w-96 h-200 flex items-center justify-center", //router
  1: "absolute inset-x-0 bottom-[15%] sm:inset-y-0 sm:left-[unset] sm:right-[12%] 2lg:right-[20%] mx-auto sm:mx-0 sm:my-auto w-52 h-57.5 sm:w-64 sm:h-70", // stack
  2: "absolute inset-x-0 bottom-[15%] sm:inset-y-0 sm:left-[unset] sm:right-[20%] mx-auto sm:mx-0 sm:my-auto w-46 h-[236px] sm:w-[264px] sm:h-85 rounded-2xl", // risk
  3: "absolute inset-x-0 bottom-[20%] sm:inset-y-0 sm:left-[unset] sm:right-[14%] 2lg:right-[20%] mx-auto sm:mx-0 sm:my-auto bg-white w-64 h-46 sm:w-72 sm:h-52 rounded-2xl border border-[#1F840D61]", // fake chart
};

const AdvisoryProcess = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [off, setoff] = useState(false);
  const handleNext = () => {
    setoff(true);
    setCurrentStep((prev) => (prev + 1) % steps.length);
  };

  const handlePrev = () => {
    setoff(true);
    setCurrentStep((prev) => (prev - 1 + steps.length) % steps.length);
  };

  const step = steps[currentStep];

  useEffect(() => {
    if (off == true) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [off]);

  const handleStartedButton = () => {
    document.getElementById("mf-guidance")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <>
      <section className="section mt-16 lg:mt-36 w-full relative">
        <h2 className="text-center font-bold leading-1.3 text-xl sm:text-2xl sm:leading-8 lg:text-3xl lg:leading-9 mb-2">
         Your Clear 4-Step Path to a Stronger Portfolio
        </h2>

        <p className="leading-1.3 text-center text-sm sm:text-base sm:leading-6  text-[#404040] mb-8">
          Four focused steps to get you started.
        </p>

        <div className="w-full max-w-md sm:max-w-full flex flex-col sm:flex-row mx-auto items-center relative bg-[#F5F5F5] border-4 border-white rounded-3xl sm:rounded-9xl shadow-[0px_10px_33px_-7px_#00000026] overflow-hidden">
          <div className="w-full h-96 sm:w-103 sm:h-108.5 2lg:w-130.25 2lg:h-129.25 shrink-0 relative">
            {DOTS_MAPPING.map((item) => (
              <span
                key={item.id}
                className={`w-2 h-2 rounded-full absolute z-2 ${item.style} ${item.id === currentStep ? "bg-black" : "bg-gray-300"}  `}
              />
            ))}

            <div
              className={`w-135 h-135 sm:w-140.5 sm:h-140.5 2lg:w-163 2lg:h-163 rounded-full absolute z-1 left-1/2 sm:left-[unset] right-0 translate-x-[-50%] sm:translate-x-0 bottom-0 sm:top-0 mx-auto sm:my-auto border border-black/20 ${BG_MAPPING[currentStep]}`}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 40, scale: 0.96 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -40, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className={STEP_WRAPPER_CLASS[currentStep]}
                >
                  {STEP_ANIMATIONS[currentStep]}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center sm:items-start justify-center md:gap-5 2lg:gap-8 mx-auto p-6 lg:p-9">
            <div className="font-bold text-2xl 2md:text-4xl flex md:mx-auto">{step.id}</div>

            <div className="text-center sm:text-left">
              <div className="text-2xl 2md:text-3xl 2md:leading-9 2lg:text-4xl 2lg:leading-10 font-bold leading-1.3 mb-4">{step.title}</div>

              <p className="text-sm sm:text-base sm:leading-6 leading-1.3 text-[#404040] mb-5 sm:mb-8">{step.description}</p>

              <button
                onClick={handleStartedButton}
                className="w-full max-w-60 sm:max-w-80 mx-auto sm:mx-0 flex items-center justify-center gap-1.5 text-[#101828] border border-[#B1B1B1] rounded-xl py-2.5 2md:py-3.5 text-sm 2md:text-base mb-5 sm:mb-0 transition hover:bg-gray-300"
              >
                Get Started
                <Icon.dropdownDownArrow className="-rotate-90" />
              </button>

              <div className="sm:absolute z-1 bottom-8 right-8 flex items-center justify-center gap-4">
                <button
                  onClick={handlePrev}
                  className="p-1.5 rounded-full flex items-center justify-center border border-[#000000] transition hover:bg-gray-200"
                >
                  <Icon.rightArrow className="-rotate-180" />
                </button>

                <div className="flex gap-2">
                  {steps.map((_, index) => (
                    <span
                      key={index}
                      className={`w-2 h-2 rounded-full transition ${index === currentStep ? "bg-black" : "bg-gray-300"}`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNext}
                  className="p-1.5 rounded-full flex items-center justify-center border border-[#000000] transition hover:bg-gray-200"
                >
                  <Icon.rightArrow />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdvisoryProcess;
